import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * @typedef {Object} AnnouncementBarRefs
 * @property {HTMLElement} viewport
 * @property {HTMLElement[]} slides
 * @property {HTMLButtonElement} [prev]
 * @property {HTMLButtonElement} [next]
 * @property {HTMLElement} [liveRegion]
 */

/**
 * @extends {Component<AnnouncementBarRefs>}
 */
class AnnouncementBarComponent extends Component {
  requiredRefs = ['viewport'];

  /** @type {number} */
  #index = 0;

  /** @type {number | null} */
  #timer = null;

  /** @type {boolean} */
  #paused = false;

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();

    const slides = this.#slides;
    if (slides.length <= 1) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener('pointerenter', this.#onPointerEnter, { signal });
    this.addEventListener('pointerleave', this.#onPointerLeave, { signal });
    this.addEventListener('focusin', this.#pause, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });
    document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });

    this.#show(0, { instant: true });
    this.#start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stop();
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * @param {number} index
   * @param {Event} [event]
   */
  goTo(index, event) {
    event?.preventDefault();
    this.#show(index);
    this.#start();
  }

  /**
   * @param {Event} [event]
   */
  previous(event) {
    event?.preventDefault();
    this.goTo(this.#index - 1, event);
  }

  /**
   * @param {Event} [event]
   */
  next(event) {
    event?.preventDefault();
    this.goTo(this.#index + 1, event);
  }

  get #slides() {
    const slides = this.refs.slides;
    return Array.isArray(slides) ? slides : [];
  }

  get #interval() {
    const value = Number(this.dataset.interval);
    return Number.isFinite(value) && value >= 1000 ? value : 3000;
  }

  get #autoplay() {
    return this.dataset.autoplay === 'true' && !prefersReducedMotion();
  }

  /**
   * @param {number} index
   * @param {{ instant?: boolean }} [options]
   */
  #show(index, { instant = false } = {}) {
    const slides = this.#slides;
    const total = slides.length;
    if (total === 0) return;

    const nextIndex = ((index % total) + total) % total;
    const reduced = prefersReducedMotion() || instant;

    for (const [i, slide] of slides.entries()) {
      const isActive = i === nextIndex;
      slide.classList.toggle('is-active', isActive);
      slide.toggleAttribute('inert', !isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      if (reduced) {
        slide.classList.toggle('is-instant', true);
      } else {
        slide.classList.remove('is-instant');
      }
    }

    this.#index = nextIndex;
    this.#announce();
  }

  #announce() {
    const liveRegion = this.refs.liveRegion;
    const slide = this.#slides[this.#index];
    if (!liveRegion || !slide) return;

    liveRegion.textContent = slide.textContent?.trim() ?? '';
  }

  #start() {
    this.#stop();
    if (!this.#autoplay || this.#paused) return;
    if (this.#slides.length <= 1) return;

    this.setAttribute('aria-live', 'off');
    this.#timer = window.setTimeout(() => {
      this.#show(this.#index + 1);
      this.#start();
    }, this.#interval);
  }

  #stop() {
    if (this.#timer) {
      window.clearTimeout(this.#timer);
      this.#timer = null;
    }
  }

  #pause = () => {
    this.#paused = true;
    this.setAttribute('aria-live', 'polite');
    this.#stop();
  };

  #resume = () => {
    this.#paused = false;
    this.#start();
  };

  #onPointerEnter = () => {
    this.#pause();
  };

  #onPointerLeave = () => {
    this.#resume();
  };

  #onFocusOut = () => {
    if (this.contains(document.activeElement)) return;
    this.#resume();
  };

  /**
   * @param {Event} event
   */
  #onBlockSelect = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !this.contains(target)) return;

    const slide = target.closest('.announcement-bar__item');
    if (!(slide instanceof HTMLElement)) return;

    const index = this.#slides.indexOf(slide);
    if (index < 0) return;

    this.goTo(index);
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBarComponent);
}
